// ============================================================
// VideoEditorPanel.jsx — top-level Video Editor workspace (admin-only, v1)
// ------------------------------------------------------------
//   No clip selected   → ClipBrowser (mock library)
//   Clip selected      → VideoCanvas + Timeline + rail (Trim | Text | Music)
//                        + Save draft / Render
//
// Persistence is client-side via the anon Supabase client under RLS
// (useVideoEditor). Render is submitted to the backend stub /api/video/render,
// which returns a mock "processing" response (Rendi integration comes later).
// Styling is scoped to `.ve-*` and reuses the global dashboard design tokens,
// matching the Photo Editor panel.
// ============================================================

import React, { useState, useEffect, useRef } from 'react';
import { useVideoEditor } from '../hooks/useVideoEditor';
import { useViewingAs } from '../hooks/useViewingAs';
import ClipBrowser from '../components/videoeditor/ClipBrowser';
import ProjectsList from '../components/videoeditor/ProjectsList';
import VideoCanvas from '../components/videoeditor/VideoCanvas';
import Timeline from '../components/videoeditor/Timeline';
import TextOverlayTool from '../components/videoeditor/TextOverlayTool';
import MusicPicker from '../components/videoeditor/MusicPicker';
import FilterPicker from '../components/videoeditor/FilterPicker';
import { fontFaceCss } from '../utils/videoFonts';

const TABS = [
  { id: 'trim',    label: 'Trim' },
  { id: 'text',    label: 'Text' },
  { id: 'filters', label: 'Filters' },
  { id: 'music',   label: 'Music' },
];

export default function VideoEditorPanel({ clientId, onNavigate, setSelectedVideoProject }) {
  const [toast, setToast] = useState({ visible: false, msg: '' });
  const [tab, setTab]     = useState('trim');
  const [view, setView]   = useState('projects'); // 'projects' | 'browser' (editor shows when a clip is loaded)
  const [playhead, setPlayhead] = useState(0);
  const [playing, setPlaying]   = useState(false);
  const [renderPanelOpen, setRenderPanelOpen] = useState(true);
  const { viewingAsClientId } = useViewingAs();

  const showToast = (msg) => {
    setToast({ visible: true, msg });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast((t) => ({ ...t, visible: false })), 2600);
  };

  const ed = useVideoEditor({ clientId, showToast });
  // Rendering or saving mid-transcription would miss the subtitles that are
  // about to land, so a transcript in flight blocks both.
  const busy = ed.isSaving || ed.subtitlesLoading;

  useEffect(() => { setRenderPanelOpen(true); }, [ed.renderStatus]);

  // Phase 7. The editor holds a whole project — clip, overlays, music, trim,
  // and the projectId it will save back to — in memory. Making its persistence
  // viewing-as aware creates a hazard that did not exist while everything was
  // scoped to the signed-in admin: switch client with a project open, press
  // Save, and the PREVIOUS client's work is written onto the NEW client's
  // account, because the routes resolve the target from the live context while
  // the editor state is still the old one.
  //
  // So a context change resets the editor to the projects list. It is a
  // deliberate discard of unsaved edits, which is the safe direction: the
  // alternative is silently attributing one client's video to another.
  //
  // Skipped on first mount — clearProject() there would fight the panel's own
  // initial state for no reason.
  const lastViewedRef = useRef(viewingAsClientId);
  useEffect(() => {
    if (lastViewedRef.current === viewingAsClientId) return;
    lastViewedRef.current = viewingAsClientId;
    ed.clearProject();
    setView('projects');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingAsClientId]);

  const handleDownload = async () => {
    try {
      const res = await fetch(ed.outputUrl);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = 'autopilot-render.mp4';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch {
      showToast('Download failed — try again');
    }
  };

  // Return to the projects list, clearing the loaded project + transport state.
  const resetView = () => {
    ed.clearProject();
    setTab('trim');
    setPlayhead(0);
    setPlaying(false);
    setView('projects');
  };

  // Leave the editor (Back / Cancel) — confirm first if there are unsaved edits.
  const handleLeaveEditor = () => {
    if (ed.isDirty && !window.confirm('You have unsaved edits — leave without saving?')) return;
    resetView();
  };

  // Start a new project: go pick/upload a source clip.
  const handleNewProject = () => setView('browser');

  // Reopen a saved project (restores full state; the editor shows once the
  // clip is loaded). Reset transport for the freshly loaded project.
  const handleOpenProject = (row) => {
    ed.loadProject(row);
    setTab('trim');
    setPlayhead(0);
    setPlaying(false);
  };

  // Back from the clip browser (no clip chosen yet) → projects list.
  const handleBrowserBack = () => setView('projects');

  // "Use in Post" — hand the completed project to the Post Generator (Video tab
  // reads it via the selectedVideoProject bridge) and navigate there.
  const handleUseInPost = (row) => {
    setSelectedVideoProject?.(row);
    onNavigate?.('imagegen');
  };

  const handleSeek = (t) => {
    setPlaying(false);
    setPlayhead(t);
  };

  return (
    <div className="ve-root">
      <style>{VE_STYLES}</style>
      <style>{fontFaceCss()}</style>

      {!ed.clip ? (
        view === 'browser' ? (
          <ClipBrowser onPick={ed.loadClip} clientId={clientId} onBack={handleBrowserBack} />
        ) : (
          <ProjectsList clientId={clientId} onOpen={handleOpenProject} onNew={handleNewProject} onUseInPost={handleUseInPost} />
        )
      ) : (
        <div className="ve-editor">
          <div className="ve-stage-col">
            <div className="ve-stage-head">
              <button className="ve-back-btn" onClick={handleLeaveEditor} title="Back to projects">← Projects</button>
              <input
                className="ve-title-input"
                value={ed.title}
                onChange={(e) => ed.setTitle(e.target.value)}
                aria-label="Project title"
              />
              {ed.renderStatus !== 'draft' && (
                <span className={`ve-status ve-status-${ed.renderStatus}`}>{ed.renderStatus}</span>
              )}
            </div>

            <VideoCanvas
              clip={ed.clip}
              overlays={ed.overlays}
              activeOverlayId={ed.activeOverlayId}
              playhead={playhead}
              playing={playing}
              trimStart={ed.trimStart}
              trimEnd={ed.trimEnd}
              filterCss={ed.filterCss}
              onTime={setPlayhead}
              onDuration={ed.setMeasuredDuration}
              onSelectOverlay={ed.selectOverlay}
              onDeselectOverlay={ed.deselectOverlay}
              onMoveOverlay={ed.updateOverlay}
            />

            <div className="ve-transport">
              <button className="ve-btn ve-btn-ghost ve-btn-sm" onClick={() => setPlaying((p) => !p)}>
                {playing ? '❚❚ Pause' : '▶ Play'}
              </button>
              <Timeline
                duration={ed.duration}
                trimStart={ed.trimStart}
                trimEnd={ed.trimEnd}
                playhead={playhead}
                overlays={ed.overlays}
                onTrimChange={ed.updateTrim}
                onSeek={handleSeek}
                onDeleteClip={ed.clearProject}
              />
            </div>

            {ed.renderStatus === 'processing' && renderPanelOpen && (
              <div className="ve-render-panel ve-render-processing">
                <span className="ve-spinner" aria-hidden="true" />
                <div className="ve-render-text">
                  <div className="ve-render-title">Rendering your video…</div>
                  <div className="ve-render-sub">
                    This can take a minute. Keep editing if you like — the finished video will appear here.
                  </div>
                </div>
                <button className="ve-render-close" onClick={() => setRenderPanelOpen(false)} title="Dismiss">×</button>
              </div>
            )}

            {ed.renderStatus === 'completed' && ed.outputUrl && renderPanelOpen && (
              <div className="ve-render-panel ve-render-complete">
                <div className="ve-render-head">
                  <span className="ve-render-badge">✓ Render Complete</span>
                  <button
                    className="ve-btn ve-btn-primary ve-btn-sm"
                    onClick={handleDownload}
                  >
                    ↓ Download
                  </button>
                  <button className="ve-render-close" onClick={() => setRenderPanelOpen(false)} title="Dismiss">×</button>
                </div>
                <video className="ve-render-video" src={ed.outputUrl} controls playsInline />
              </div>
            )}

            {ed.renderStatus === 'failed' && renderPanelOpen && (
              <div className="ve-render-panel ve-render-failed">
                <div className="ve-render-head">
                  <div className="ve-render-title">Render failed</div>
                  <button className="ve-render-close" onClick={() => setRenderPanelOpen(false)} title="Dismiss">×</button>
                </div>
                <div className="ve-render-error">
                  {ed.errorMessage || 'Something went wrong while rendering. Please try again.'}
                </div>
              </div>
            )}
          </div>

          <div className="ve-rail">
            <div className="ve-tabs">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  className={`ve-tab${tab === t.id ? ' active' : ''}`}
                  onClick={() => setTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="ve-tab-body">
              {tab === 'trim' && (
                <div className="ve-trim-tab">
                  <p className="ve-hint">
                    Drag the handles on the timeline to set where the video starts and ends.
                  </p>
                  <div className="ve-field-label">Start (seconds)</div>
                  <div className="ve-slider-row">
                    <input
                      className="ve-range" type="range" min={0} max={ed.duration || 0} step={0.1}
                      value={ed.trimStart}
                      onChange={(e) => ed.updateTrim({ start: Math.min(Number(e.target.value), ed.trimEnd - 0.1) })}
                    />
                    <span className="ve-slider-val">{ed.trimStart.toFixed(1)}s</span>
                  </div>
                  <div className="ve-field-label">End (seconds)</div>
                  <div className="ve-slider-row">
                    <input
                      className="ve-range" type="range" min={0} max={ed.duration || 0} step={0.1}
                      value={ed.trimEnd}
                      onChange={(e) => ed.updateTrim({ end: Math.max(Number(e.target.value), ed.trimStart + 0.1) })}
                    />
                    <span className="ve-slider-val">{ed.trimEnd.toFixed(1)}s</span>
                  </div>
                </div>
              )}

              {tab === 'text' && (
                <TextOverlayTool
                  overlays={ed.overlays}
                  activeId={ed.activeOverlayId}
                  duration={ed.duration}
                  onAdd={() => ed.addOverlay(playhead)}
                  onSelect={ed.toggleOverlay}
                  onUpdate={ed.updateOverlay}
                  onRemove={ed.removeOverlay}
                  onAddSubtitles={ed.addSubtitles}
                  subtitlesLoading={ed.subtitlesLoading}
                />
              )}

              {tab === 'filters' && (
                <FilterPicker
                  activePreset={ed.filterPreset}
                  onSelect={ed.selectFilter}
                />
              )}

              {tab === 'music' && (
                <MusicPicker
                  music={ed.music}
                  onSelect={ed.selectTrack}
                  onUpdate={ed.updateMusic}
                  clientId={clientId}
                  showToast={showToast}
                />
              )}
            </div>

            <div className="ve-actions">
              <button
                className="ve-btn ve-btn-primary ve-btn-block"
                disabled={busy}
                onClick={ed.submitRender}
              >
                {busy ? 'Working…' : 'Render Video'}
              </button>
              <div className="ve-actions-row">
                <button className="ve-btn ve-btn-ghost" disabled={busy} onClick={ed.saveProject}>
                  Save Draft
                </button>
                <button className="ve-btn ve-btn-ghost" disabled={busy} onClick={handleLeaveEditor}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`ve-toast${toast.visible ? ' show' : ''}`}>{toast.msg}</div>
    </div>
  );
}

// ── Scoped styles (reuse global design tokens) ──────────────────────────────
const VE_STYLES = `
.ve-root { height:100%; color:var(--text-primary); }
.ve-hint { font-size:11px; color:var(--text-muted); margin:4px 0 12px; line-height:1.4; }
.ve-mock-tag { font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.05em;
  color:var(--accent); background:var(--accent-dim); border:1px solid var(--accent-border);
  padding:2px 7px; border-radius:6px; margin-left:6px; }

/* Browser */
.ve-browser-toolbar { display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; gap:12px; flex-wrap:wrap; }
.ve-browser-title { font-size:15px; font-weight:600; }
.ve-browser-actions { display:flex; align-items:center; gap:8px; }
.ve-upload-bar { height:6px; border-radius:4px; background:var(--bg-surface); border:1px solid var(--border);
  overflow:hidden; margin-bottom:14px; }
.ve-upload-fill { height:100%; background:var(--accent); transition:width .15s ease; }
.ve-browser-error { font-size:12px; color:#ff6b6b; background:rgba(255,77,77,0.08);
  border:1px solid rgba(255,77,77,0.22); border-radius:8px; padding:9px 11px; margin-bottom:14px; word-break:break-word; }
.ve-browser-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center;
  padding:48px 20px; border:1px dashed var(--border); border-radius:14px; background:var(--bg-surface); }
.ve-empty-icon { font-size:34px; margin-bottom:10px; }
.ve-empty-title { font-size:15px; font-weight:600; color:var(--text-primary); }
.ve-empty-sub { font-size:12px; color:var(--text-muted); margin-top:6px; max-width:320px; line-height:1.5; }
.ve-clip-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(220px, 1fr)); gap:14px; }
.ve-clip-tile { cursor:pointer; transition:opacity .15s, transform .15s; }
.ve-clip-tile.dragging { opacity:.4; }
.ve-clip-tile.drag-over .ve-clip-thumb { outline:2px dashed var(--accent); outline-offset:2px; }
.ve-clip-thumb { position:relative; aspect-ratio:16/9; border-radius:10px; overflow:hidden;
  background:var(--bg-surface); box-shadow:0 2px 8px rgba(0,0,0,0.25); border:2px solid transparent; }
.ve-clip-tile:hover .ve-clip-thumb { border-color:var(--accent); }
.ve-clip-thumb img { width:100%; height:100%; object-fit:cover; display:block; }
.ve-clip-dur { position:absolute; bottom:6px; right:6px; font-size:10px; font-weight:700; color:#fff;
  background:rgba(0,0,0,0.6); padding:2px 6px; border-radius:6px; }
.ve-clip-aspect { position:absolute; top:6px; left:6px; font-size:9px; font-weight:700; text-transform:uppercase;
  color:var(--text-secondary); background:rgba(0,0,0,0.55); padding:2px 6px; border-radius:6px; }
.ve-clip-hover { position:absolute; inset:0; display:flex; align-items:center; justify-content:center;
  background:rgba(0,0,0,0.35); opacity:0; transition:opacity .15s; }
.ve-clip-tile:hover .ve-clip-hover { opacity:1; }
.ve-clip-del { position:absolute; top:6px; right:6px; z-index:2; width:22px; height:22px; border-radius:50%;
  background:rgba(0,0,0,0.7); border:none; color:#fff; font-size:11px; line-height:1;
  display:flex; align-items:center; justify-content:center; cursor:pointer; padding:0;
  opacity:0; transition:opacity .15s, background .15s; }
.ve-clip-tile:hover .ve-clip-del { opacity:1; }
.ve-clip-del:hover { background:#e55; }
.ve-clip-name { font-size:12px; color:var(--text-secondary); margin-top:7px; }
.ve-clip-name-row { display:flex; align-items:center; gap:4px; margin-top:7px; }
.ve-clip-name-row .ve-clip-name { margin-top:0; flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.ve-clip-name-row .ve-icon-btn { opacity:0; transition:opacity .15s; }
.ve-clip-tile:hover .ve-clip-name-row .ve-icon-btn { opacity:1; }
.ve-clip-rename-input { display:block; width:100%; box-sizing:border-box; margin-top:7px; }
.ve-library-only-toggle { display:flex; align-items:center; gap:6px; font-size:11px; color:var(--text-secondary);
  cursor:pointer; user-select:none; white-space:nowrap; }
.ve-library-only-toggle input { cursor:pointer; }

/* Editor layout */
.ve-editor { display:flex; gap:16px; height:100%; align-items:stretch; }
.ve-stage-col { flex:1; min-width:0; display:flex; flex-direction:column; gap:12px; }
.ve-stage-head { display:flex; align-items:center; gap:10px; }
.ve-title-input { flex:1; background:transparent; border:none; border-bottom:1px solid transparent;
  color:var(--text-primary); font-size:15px; font-weight:600; padding:4px 2px; font-family:var(--font-head); }
.ve-title-input:hover { border-bottom-color:var(--border); }
.ve-title-input:focus { outline:none; border-bottom-color:var(--accent-border); }
.ve-status { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.05em;
  padding:3px 9px; border-radius:7px; }
.ve-status-processing { color:var(--accent); background:var(--accent-dim); border:1px solid var(--accent-border); }
.ve-status-completed  { color:#31DCBA; background:rgba(49,220,186,0.15); }
.ve-status-failed     { color:#ff6b6b; background:rgba(255,77,77,0.15); }

/* Canvas / stage */
.ve-canvas-wrap { width:100%; flex:1; display:flex; align-items:center; justify-content:center; min-height:0; }
.ve-stage { position:relative; max-width:100%; max-height:60vh; line-height:0;
  border-radius:12px; overflow:hidden; box-shadow:0 8px 32px rgba(0,0,0,0.4); background:#000; }
.ve-video { max-width:100%; max-height:60vh; display:block; object-fit:contain; background:#000; }
.ve-overlay-layer { position:absolute; inset:0; pointer-events:none; }
.ve-overlay { position:absolute; transform:translate(-50%,-50%); white-space:pre-wrap; line-height:1.2;
  font-weight:800; font-family:var(--font-head); text-shadow:0 2px 8px rgba(0,0,0,0.6); cursor:grab;
  pointer-events:auto; padding:2px 6px; max-width:90%; word-break:break-word; touch-action:none; }
.ve-overlay.active { outline:1.5px dashed var(--accent); outline-offset:3px; }

/* Transport + timeline */
.ve-transport { display:flex; align-items:center; gap:12px; }
.ve-timeline { flex:1; }
.ve-tl-times { display:flex; align-items:center; justify-content:space-between; font-size:11px;
  color:var(--text-secondary); margin-bottom:6px; }
.ve-tl-dur { color:var(--accent); font-weight:600; }
.ve-tl-track { position:relative; height:36px; border-radius:9px; background:var(--bg-surface);
  border:1px solid var(--border); cursor:pointer; overflow:visible; }
.ve-tl-fills { position:absolute; inset:0; border-radius:9px; overflow:hidden; }
.ve-tl-dim { position:absolute; top:0; bottom:0; background:rgba(0,0,0,0.45); pointer-events:none; }
.ve-tl-sel { position:absolute; top:0; bottom:0; background:var(--accent-dim); border-left:2px solid var(--accent);
  border-right:2px solid var(--accent); pointer-events:none; }
.ve-tl-marker { position:absolute; top:2px; height:6px; background:var(--accent); opacity:0.8;
  border-radius:3px; pointer-events:none; }
.ve-tl-handle { position:absolute; top:-3px; bottom:-3px; width:12px; margin-left:-6px; cursor:ew-resize;
  background:var(--accent); border-radius:4px; box-shadow:0 0 0 1px rgba(0,0,0,0.35), 0 1px 3px rgba(0,0,0,0.4);
  z-index:5; touch-action:none; }
.ve-tl-handle:hover { background:#28c4a8; }
.ve-tl-handle::after { content:''; position:absolute; top:50%; left:50%; transform:translate(-50%,-50%);
  width:2px; height:16px; background:var(--bg-base); border-radius:1px; }
.ve-tl-playhead { position:absolute; top:0; bottom:0; width:2px; margin-left:-1px; background:#fff;
  z-index:4; cursor:ew-resize; box-shadow:0 0 4px rgba(0,0,0,0.6); }

/* Right rail (glass card) */
.ve-rail { width:320px; min-width:320px; display:flex; flex-direction:column;
  background:rgba(20,23,32,0.7); backdrop-filter:blur(12px); border:1px solid var(--border);
  border-radius:14px; overflow:hidden; }
.ve-tabs { display:flex; padding:8px; gap:6px; border-bottom:1px solid var(--border); }
.ve-tab { flex:1; padding:8px; border-radius:8px; border:none; cursor:pointer; font-size:12px; font-weight:600;
  background:transparent; color:var(--text-secondary); transition:background .15s, color .15s; }
.ve-tab.active { background:var(--accent-dim); color:var(--accent); }
.ve-tab-body { flex:1; overflow-y:auto; padding:12px; }

/* Field labels / chips / sliders */
.ve-field-label { font-size:10px; font-weight:700; letter-spacing:.05em; text-transform:uppercase;
  color:var(--text-muted); margin:14px 0 6px; display:flex; align-items:center; }
.ve-field-label:first-child { margin-top:0; }
.ve-chip-row { display:flex; flex-wrap:wrap; gap:6px; }
.ve-chip { background:var(--bg-base); border:1px solid var(--border); color:var(--text-secondary);
  font-size:11px; padding:6px 10px; border-radius:7px; cursor:pointer; }
.ve-chip.active { border-color:var(--accent); color:var(--accent); background:var(--accent-dim); }
.ve-slider-row { display:flex; align-items:center; gap:10px; }
.ve-slider-val { font-size:11px; color:var(--accent); width:46px; text-align:right; font-variant-numeric:tabular-nums; }
.ve-link-btn { background:none; border:none; color:var(--accent); font-size:11px; font-weight:600; cursor:pointer; padding:6px 0 0; }

/* Range inputs */
.ve-range { -webkit-appearance:none; appearance:none; width:100%; height:4px; border-radius:3px;
  background:rgba(255,255,255,0.12); outline:none; cursor:pointer; margin:6px 0; }
.ve-range::-webkit-slider-thumb { -webkit-appearance:none; appearance:none; width:14px; height:14px;
  border-radius:50%; background:var(--accent); border:2px solid var(--bg-base); box-shadow:0 0 0 1px var(--accent); }
.ve-range::-moz-range-thumb { width:14px; height:14px; border-radius:50%; background:var(--accent); border:2px solid var(--bg-base); }

/* Text overlays */
.ve-text-input { width:100%; box-sizing:border-box; background:var(--bg-base); border:1px solid var(--border);
  border-radius:8px; color:var(--text-primary); font-size:12px; padding:8px 10px; resize:vertical; }
.ve-text-input:focus { outline:none; border-color:var(--accent-border); }
.ve-layer-list { display:flex; flex-direction:column; gap:6px; margin-top:10px; }
.ve-layer-row { display:flex; align-items:center; gap:8px; cursor:pointer;
  background:var(--bg-base); border:1px solid var(--border); border-radius:8px; padding:7px 10px; }
.ve-layer-row.active { border-color:var(--accent-border); background:var(--accent-dim); }
.ve-layer-text { flex:1; font-size:12px; color:var(--text-primary); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.ve-layer-time { font-size:10px; color:var(--text-muted); font-variant-numeric:tabular-nums; white-space:nowrap; }
.ve-layer-del { background:none; border:none; color:var(--text-secondary); font-size:16px; cursor:pointer; line-height:1; }
.ve-text-editor { margin-top:14px; }
.ve-time-row { display:flex; gap:10px; }
.ve-time-field { flex:1; display:flex; flex-direction:column; gap:4px; font-size:10px; color:var(--text-muted);
  text-transform:uppercase; letter-spacing:.05em; font-weight:700; }
.ve-time-field input { background:var(--bg-base); border:1px solid var(--border); border-radius:8px;
  color:var(--text-primary); font-size:12px; padding:7px 9px; }
.ve-time-field input:focus { outline:none; border-color:var(--accent-border); }
.ve-swatches { display:flex; flex-wrap:wrap; gap:6px; align-items:center; }
.ve-swatch { width:22px; height:22px; border-radius:50%; border:2px solid var(--border); cursor:pointer; padding:0; }
.ve-swatch.active { border-color:var(--accent); transform:scale(1.1); }
.ve-align-row { display:flex; gap:6px; }
.ve-align-btn { flex:1; background:var(--bg-base); border:1px solid var(--border); color:var(--text-secondary);
  font-size:13px; padding:6px; border-radius:7px; cursor:pointer; }
.ve-align-btn.active { border-color:var(--accent); color:var(--accent); background:var(--accent-dim); }

/* Music picker */
.ve-track-list { display:flex; flex-direction:column; gap:8px; }
.ve-track { display:block; width:100%; text-align:left; cursor:pointer; background:var(--bg-base);
  border:1px solid var(--border); border-radius:10px; padding:10px; transition:border-color .15s, background .15s; }
.ve-track:hover { border-color:var(--accent-border); }
.ve-track.selected { border-color:var(--accent); background:var(--accent-dim); }
.ve-track-head { display:flex; align-items:center; gap:10px; margin-bottom:8px; }
.ve-track-play { width:26px; height:26px; flex:0 0 auto; border-radius:50%; display:flex; align-items:center;
  justify-content:center; font-size:13px; color:var(--accent); background:var(--accent-dim);
  border:1px solid var(--accent-border); }
.ve-track-meta { display:flex; flex-direction:column; min-width:0; }
.ve-track-title { font-size:12px; font-weight:600; color:var(--text-primary); }
.ve-track-sub { font-size:10px; color:var(--text-muted); }
.ve-wave { display:flex; align-items:flex-end; gap:1px; height:30px; width:100%; }
.ve-wave-bar { flex:1; min-width:1px; background:var(--text-muted); opacity:0.45; border-radius:1px; }
.ve-wave.active .ve-wave-bar { background:var(--accent); opacity:0.85; }
.ve-music-controls { margin-top:14px; }

/* Filters */
.ve-filter-grid { display:grid; grid-template-columns:repeat(3, 1fr); gap:8px; }
.ve-filter-item { display:flex; flex-direction:column; gap:5px; align-items:stretch; padding:0; cursor:pointer;
  background:none; border:none; }
.ve-filter-swatch { display:block; width:100%; height:46px; border-radius:7px;
  background:linear-gradient(135deg, #6a5acd 0%, #ff8c42 50%, #2ec4b6 100%);
  border:2px solid var(--border); box-sizing:border-box; }
.ve-filter-item:hover .ve-filter-swatch { border-color:var(--accent-border); }
.ve-filter-item.active .ve-filter-swatch { border-color:var(--accent); box-shadow:0 0 0 1px var(--accent); }
.ve-filter-name { font-size:10px; color:var(--text-secondary); text-align:center; white-space:nowrap;
  overflow:hidden; text-overflow:ellipsis; }
.ve-filter-item.active .ve-filter-name { color:var(--accent); font-weight:600; }

/* Actions */
.ve-actions { padding:12px; border-top:1px solid var(--border); display:flex; flex-direction:column; gap:8px; }
.ve-actions-row { display:flex; gap:8px; }
.ve-actions-row .ve-btn { flex:1; }

/* Buttons (mirror .pe-btn) */
.ve-btn { padding:9px 14px; border-radius:9px; font-size:12px; font-weight:600; cursor:pointer;
  border:1px solid transparent; transition:background .15s, color .15s, border-color .15s; }
.ve-btn:disabled { opacity:.55; cursor:not-allowed; }
.ve-btn-sm { padding:6px 12px; font-size:11px; }
.ve-btn-block { width:100%; margin-top:4px; }
.ve-btn-primary { background:var(--accent); color:var(--bg-base); }
.ve-btn-primary:hover:not(:disabled) { background:#28c4a8; }
.ve-btn-ghost { background:transparent; color:var(--text-secondary); border-color:var(--border); }
.ve-btn-ghost:hover:not(:disabled) { color:var(--text-primary); border-color:var(--accent-border); }
.ve-btn-accent { background:transparent; color:var(--accent); border-color:var(--accent-border); }
.ve-btn-accent:hover:not(:disabled) { background:var(--accent-dim); }

/* Render result states (processing / completed / failed) */
.ve-render-panel { margin-top:4px; padding:14px; border-radius:14px; border:1px solid var(--border);
  background:rgba(20,23,32,0.7); backdrop-filter:blur(12px); }
.ve-render-processing { display:flex; align-items:center; gap:14px; }
.ve-spinner { width:22px; height:22px; flex:0 0 auto; border-radius:50%;
  border:2px solid var(--accent-dim); border-top-color:var(--accent); animation:ve-spin .8s linear infinite; }
@keyframes ve-spin { to { transform:rotate(360deg); } }
.ve-render-text { display:flex; flex-direction:column; gap:3px; min-width:0; }
.ve-render-title { font-size:13px; font-weight:600; color:var(--text-primary); }
.ve-render-sub { font-size:11px; color:var(--text-muted); line-height:1.4; }
.ve-render-head { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:12px; }
.ve-render-badge { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em;
  color:var(--accent); background:var(--accent-dim); border:1px solid var(--accent-border);
  padding:4px 10px; border-radius:8px; }
.ve-render-video { width:100%; max-height:50vh; display:block; border-radius:10px; background:#000;
  box-shadow:0 8px 28px rgba(0,0,0,0.4); }
.ve-render-complete { border-color:var(--accent-border); }
.ve-render-failed { border-color:rgba(255,107,107,0.5); }
.ve-render-failed .ve-render-title { color:#ff6b6b; }
.ve-render-error { margin-top:8px; font-size:12px; color:var(--text-secondary); line-height:1.45;
  background:rgba(255,77,77,0.08); border:1px solid rgba(255,77,77,0.22); border-radius:8px;
  padding:9px 11px; word-break:break-word; }
.ve-render-panel a.ve-btn { text-decoration:none; display:inline-flex; align-items:center; }
.ve-render-close { background:none; border:none; color:var(--text-muted); font-size:20px; line-height:1;
  cursor:pointer; padding:0; margin-left:auto; flex-shrink:0; }
.ve-render-close:hover { color:var(--text-primary); }
.ve-tl-del { background:none; border:none; color:var(--text-muted); font-size:12px; line-height:1;
  cursor:pointer; padding:0 0 0 8px; flex-shrink:0; }
.ve-tl-del:hover { color:#ff6b6b; }

/* Select dropdowns (font / animation) */
.ve-select { width:100%; box-sizing:border-box; background:var(--bg-base); border:1px solid var(--border);
  border-radius:8px; color:var(--text-primary); font-size:12px; padding:8px 10px; cursor:pointer;
  appearance:none; -webkit-appearance:none; margin-top:2px; }
.ve-select:focus { outline:none; border-color:var(--accent-border); }

/* Music: selected check */
.ve-track-check { margin-left:auto; color:var(--accent); font-size:13px; font-weight:700; }

/* Back button (editor → projects) */
.ve-back-btn { background:transparent; border:1px solid var(--border); color:var(--text-secondary);
  font-size:12px; font-weight:600; padding:5px 11px; border-radius:7px; cursor:pointer; white-space:nowrap;
  transition:color .15s, border-color .15s; }
.ve-back-btn:hover { color:var(--text-primary); border-color:var(--accent-border); }

/* Projects list */
.ve-proj-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(260px, 1fr)); gap:14px; }
.ve-proj-card { cursor:pointer; background:var(--bg-surface); border:1px solid var(--border);
  border-radius:12px; padding:14px; display:flex; flex-direction:column; gap:8px;
  transition:border-color .15s, transform .15s; }
.ve-proj-card:hover { border-color:var(--accent); transform:translateY(-1px); }
.ve-proj-head { display:flex; align-items:center; justify-content:space-between; gap:10px; }
.ve-proj-title { font-size:14px; font-weight:600; color:var(--text-primary); white-space:nowrap;
  overflow:hidden; text-overflow:ellipsis; }
.ve-proj-sub { font-size:11px; color:var(--text-muted); min-height:15px; line-height:1.4;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.ve-proj-foot { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-top:2px; }
.ve-proj-date { font-size:10px; color:var(--text-secondary); }
.ve-proj-foot-actions { display:flex; align-items:center; gap:2px; }
.ve-proj-del { background:none; border:none; color:var(--text-muted); font-size:13px; line-height:1;
  cursor:pointer; padding:2px 4px; border-radius:5px; transition:color .15s, background .15s; }
.ve-proj-del:hover { color:#ff6b6b; background:rgba(255,77,77,0.1); }

/* Rename (projects + clip library) */
.ve-icon-btn { background:none; border:none; color:var(--text-muted); font-size:12px; line-height:1;
  cursor:pointer; padding:2px 4px; border-radius:5px; transition:color .15s, background .15s; }
.ve-icon-btn:hover { color:var(--accent); background:var(--accent-dim); }
.ve-rename-input { flex:1; min-width:0; background:var(--bg-base); border:1px solid var(--accent-border);
  border-radius:6px; color:var(--text-primary); font-size:13px; font-weight:600; padding:3px 7px;
  font-family:var(--font-head); }
.ve-rename-input:focus { outline:none; border-color:var(--accent); }
.ve-proj-thumb { position:relative; width:100%; aspect-ratio:16/9; border-radius:9px; overflow:hidden;
  background:#000; margin-bottom:2px; }
.ve-proj-thumb img { width:100%; height:100%; object-fit:cover; display:block; }
.ve-proj-thumb-placeholder { width:100%; height:100%; display:flex; align-items:center; justify-content:center;
  background:radial-gradient(circle at center, #1c2230 0%, #0c0e13 100%); }
.ve-proj-play { color:rgba(255,255,255,0.78); font-size:22px; line-height:1;
  width:42px; height:42px; border-radius:50%; display:flex; align-items:center; justify-content:center;
  background:rgba(255,255,255,0.08); border:1px solid rgba(255,255,255,0.18); padding-left:3px; }
.ve-proj-actions { display:flex; gap:8px; margin-top:4px; }
.ve-proj-actions .ve-btn { flex:1; text-align:center; text-decoration:none; }

/* Toast */
.ve-toast { position:fixed; bottom:24px; right:24px; z-index:1100; max-width:320px;
  background:rgba(20,23,32,0.95); border:1px solid var(--accent-border); border-left:3px solid var(--accent);
  color:var(--text-primary); font-size:12px; padding:11px 16px; border-radius:10px; box-shadow:0 8px 28px rgba(0,0,0,0.45);
  opacity:0; transform:translateY(10px); pointer-events:none; transition:opacity .2s, transform .2s; }
.ve-toast.show { opacity:1; transform:translateY(0); }

@media (max-width: 820px) {
  .ve-editor { flex-direction:column; }
  .ve-rail { width:100%; min-width:0; }
  .ve-stage, .ve-video { max-height:42vh; }
}
`;
